import type { ReactNode } from "react";
import { useLocation } from "react-router";

import {
  type AdminRouteAccessArgs,
  evaluateAdminRouteAccess,
  findAdminRouteByPathname,
} from "@/routing/adminRoutes";
import { AdminVNextShell } from "@/vnext/app-shell/AdminVNextShell";
import { useWorkspaceChromeLogging } from "@/vnext/routes/useWorkspaceChromeLogging";
import {
  NO_WORKSPACE_TABS,
  useAdminNavPageTabStrip,
} from "@/vnext/shared/workspace/useAdminNavPageTabStrip";
import { WorkspaceTabNav } from "@/vnext/shared/workspace/WorkspaceTabNav";
import "@/vnext/shared/workspace/workspaceChrome.css";

interface ProductWorkspaceChromeProps {
  actions?: ReactNode;
  children: ReactNode;
  subtitle: string;
  title: string;
}

export const isProductWorkspacePathAccessible = (
  path: string,
  routeAccessArgs?: AdminRouteAccessArgs,
): boolean => {
  const route = findAdminRouteByPathname(path);

  return route
    ? evaluateAdminRouteAccess(route, routeAccessArgs).canAccess
    : false;
};

export function ProductWorkspaceChrome({
  actions,
  children,
  subtitle,
  title,
}: ProductWorkspaceChromeProps) {
  const location = useLocation();
  // §11 page tabs: Access (Entitlements, AI insights access, Demo access) on
  // every Access tab; Feature flags has no tabs, so it shows none (#4903 M1).
  const strip = useAdminNavPageTabStrip(location.pathname);
  const logTabClick = useWorkspaceChromeLogging({
    activePath: strip?.activePath ?? "",
    componentName: "ProductWorkspaceChrome",
    pathname: location.pathname,
    tabs: strip?.tabs ?? NO_WORKSPACE_TABS,
    workspaceLabel: "product",
  });

  return (
    <AdminVNextShell
      actions={actions}
      breadcrumbs={[
        { label: "Dashboard", path: "/dashboard" },
        { label: "Product" },
        { label: title },
      ]}
      subtitle={subtitle}
      title={title}
    >
      <div className="admin-workspace-page">
        {strip ? (
          <WorkspaceTabNav
            activePath={strip.activePath}
            ariaLabel={strip.ariaLabel}
            onTabClick={logTabClick}
            tabs={strip.tabs}
          />
        ) : null}
        {children}
      </div>
    </AdminVNextShell>
  );
}
